// The Products step: the project's Shopify products (their options and variants come from Shopify), linking and
// unlinking them (project.link_product / project.unlink_product: the variants join or leave the project), and each
// product's BOM. The variants are one table (DataTable: views, columns, Copy / CSV), a band per product carrying its
// options, its BOM and Unlink.
import { Link2, Plus, Search, Unlink } from "lucide-react";
import { useMemo, useState } from "react";
import { type BomItem, revisionState } from "../../../../shared/boms.ts";
import type { Bom, Item, Project } from "../../../../shared/db.gen.ts";
import { marginOf, type ScopeProduct } from "../../../../shared/projects.ts";
import { bomLink, createBom, STATE_TONE } from "../../lib/boms.ts";
import { money } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { Link, setSearch } from "../../lib/router.tsx";
import { store, useRows } from "../../lib/sync.ts";
import { Badge, Button, Input, Thumb } from "../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../ui/DataTable.tsx";
import { PageSection } from "../../ui/PageParts.tsx";
import { toast } from "../../ui/toast.tsx";
import { Card, StepGaps, type TabProps } from "./Workspace.tsx";

const pct = (m: number | null) => (m == null ? "—" : `${Math.round(m * 100)}%`);

export function ProductsTab(props: TabProps) {
  const { project, scope, px, r, here } = props;
  const [picking, setPicking] = useState(false);
  const canLink = can("project.link_product");
  return (
    <div className="flex flex-col gap-3 p-4">
      <StepGaps r={r} steps={["products"]} project={project} px={px} here={here} />
      {scope.products.length > 0 && <Variants props={props} />}
      {!scope.products.length && !picking && (
        <div className="rounded-lg border border-dashed border-line-strong bg-surface p-6 text-center text-muted">
          No Shopify product is linked yet. Link the product this project makes: its variants join the project and its options (like Connection type and Color)
          drive the build.
        </div>
      )}
      {scope.unlinked.length > 0 && (
        <Card title="Variants in this project whose product isn't linked" note="Link the product so all its variants follow it">
          {scope.unlinked.map((u) => (
            <div key={u.productId} className="flex items-center gap-2 border-b border-line px-4 py-2 last:border-0">
              <span className="min-w-0 flex-1 truncate">{u.title}</span>
              <span className="text-[12px] text-muted">{u.variants} variants here</span>
              {canLink && (
                <Button size="sm" icon={<Link2 className="size-3.5" />} onClick={() => link(project.id, u.productId)}>
                  Link
                </Button>
              )}
            </div>
          ))}
        </Card>
      )}
      {canLink &&
        (picking ? (
          <ProductPicker projectId={project.id} onClose={() => setPicking(false)} />
        ) : (
          <div>
            <Button icon={<Plus className="size-4" />} onClick={() => setPicking(true)}>
              Link a Shopify product
            </Button>
          </div>
        ))}
    </div>
  );
}

/**
 * Links a product. Variants or BOMs of it that are in another project move only after asking (the server refuses
 * without `move`); unlinking later puts the variants back where they were.
 */
async function link(projectId: string, productId: string) {
  const items = store.rows("item") as unknown as Item[];
  const boms = store.rows("bom") as unknown as Bom[];
  const projects = store.rows("project") as unknown as Project[];
  const name = (id: string | null) => projects.find((p) => p.id === id)?.name ?? "another project";
  const moving = items.filter((i) => i.shopify_product_id === productId && i.source === "shopify" && !i.archived && i.project_id && i.project_id !== projectId);
  const bomsElsewhere = boms.filter((b) => b.shopify_product_id === productId && b.status !== "retired" && b.project_ids.some((p) => p !== projectId));
  let move = false;
  if (moving.length || bomsElsewhere.length) {
    const from = [...new Set([...moving.map((i) => i.project_id), ...bomsElsewhere.flatMap((b) => b.project_ids)])].map(name).join(", ");
    const skus = moving.map((i) => i.sku);
    const what = [
      moving.length && `${moving.length === 1 ? "Variant" : `${moving.length} variants`} ${skus.slice(0, 8).join(", ")}${skus.length > 8 ? "…" : ""}`,
      bomsElsewhere.length && `${moving.length ? "its" : "Its"} BOM`,
    ]
      .filter(Boolean)
      .join(" and ");
    if (
      !confirm(
        `${what} ${moving.length + bomsElsewhere.length === 1 ? "is" : "are"} in ${from}.\n\nMove them to ${name(projectId)}? Unlinking the product later puts the variants back.`,
      )
    )
      return;
    move = true;
  }
  const res = await mutateWithResult("project.link_product", { id: crypto.randomUUID(), project_id: projectId, shopify_product_id: productId, move });
  const out = res.result as { moved?: { sku: string; from: string }[] } | undefined;
  if (out?.moved?.length) toast.ok(`Linked; ${out.moved.length} variants moved from ${[...new Set(out.moved.map((m) => m.from))].join(", ")}`);
}

/** A product's variant, or (`v` missing) the line saying it has none live, so its band shows. */
type VariantRow = { id: string; p: ScopeProduct; v?: BomItem; position: number };

/** Every linked product's variants, a band per product with its options, its BOM and Unlink. */
function Variants({ props }: { props: TabProps }) {
  const { px, here, scope, project } = props;
  const items = useRows("item");
  const photos = useMemo(() => new Map(items.map((i) => [i.id, i.photo_url])), [items]);
  const rows = useMemo<VariantRow[]>(
    () =>
      scope.products.flatMap((p) =>
        p.variants.length ? p.variants.map((v, i) => ({ id: v.id, p, v, position: i + 1 })) : [{ id: `${p.link.id}:none`, p, position: 0 }],
      ),
    [scope],
  );
  const bands = new Map(
    scope.products.map((p, order): [string, TableSection] => [p.link.id, { key: p.link.id, order, label: <ProductBand p={p} props={props} /> }]),
  );
  const columns: Column<VariantRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(130px,170px)",
      value: (x) => x.v?.sku ?? "",
      cell: (x) =>
        x.v && (
          <>
            <Thumb src={photos.get(x.v.id)} size={22} />
            <span className="truncate font-mono text-[12px]">{x.v.sku}</span>
          </>
        ),
    },
    {
      key: "variant",
      header: "Name",
      width: "minmax(180px,1fr)",
      value: (x) => (x.v ? x.v.shopify_variant_title || x.v.name : ""),
      cell: (x) => {
        if (!x.v) return <span className="text-muted">No live variants in Shopify.</span>;
        const item = px.itemById.get(x.v.id);
        return (
          <>
            <span className="truncate">{x.v.shopify_variant_title || x.v.name}</span>
            {item && item.project_id !== project.id && <span className="shrink-0 text-[11px] text-warn">not in this project</span>}
          </>
        );
      },
    },
    {
      key: "position",
      header: "#",
      headerTitle: "Its place among the product's variants",
      width: "56px",
      align: "right",
      optional: true,
      value: (x) => x.position || null,
    },
    {
      key: "price",
      header: "Price",
      width: "90px",
      align: "right",
      value: (x) => x.v?.price,
      cell: (x) => x.v && <span className="font-mono text-[12px]">{money(x.v.price)}</span>,
    },
    {
      key: "std_cost",
      header: "Shopify cost",
      width: "100px",
      align: "right",
      value: (x) => x.v?.std_cost,
      cell: (x) => x.v && <span className="font-mono text-[12px]">{money(x.v.std_cost)}</span>,
    },
    {
      key: "margin",
      header: "Margin",
      width: "80px",
      align: "right",
      value: (x) => (x.v ? marginOf(x.v) : null),
      text: (x) => (x.v ? pct(marginOf(x.v)) : ""),
      cell: (x) => x.v && <span className="font-mono text-[12px]">{pct(marginOf(x.v))}</span>,
    },
  ];
  return (
    <PageSection
      title="Linked products"
      foot={
        <>
          Margin here is the Sell items one (Shopify's price and cost); Costs has it against the BOM roll-up.{" "}
          <Link to={`${here.split("?")[0]}?tab=costs`} className="text-accent-text hover:underline">
            Costs
          </Link>
        </>
      }
    >
      {(slot) => (
        <DataTable
          fit={20}
          rows={rows}
          columns={columns}
          sectionOf={(x) => bands.get(x.p.link.id) ?? null}
          defaultSort={{ key: "position" }}
          onOpen={(x) => x.v && setSearch({ open: x.v.id })}
          exportName="Variants"
          bar={slot}
          pageFilters={false}
          rowNoun={["variant", "variants"]}
        />
      )}
    </PageSection>
  );
}

/** A product's band: its title, handle, status and variants, its options, its BOM (or Start its BOM) and Unlink. */
function ProductBand({ p, props }: { p: ScopeProduct; props: TabProps }) {
  const ix = props.px.ix;
  const state = p.bom ? revisionState(ix, p.bom) : null;
  const note = [p.product?.handle, p.product?.status?.toLowerCase(), `${p.variants.length} variants`].filter(Boolean).join(" · ");
  const options = p.options.map((o) => `${o.name}: ${o.values.join(", ")}`).join(" · ");
  return (
    // Keys on its buttons are theirs, not the table's
    <span
      role="presentation"
      onKeyDown={(e) => e.stopPropagation()}
      className="flex max-w-[calc(100cqw-4rem)] min-w-0 items-end gap-3 overflow-hidden font-normal tracking-normal whitespace-nowrap normal-case"
    >
      <span className="shrink-0 text-[13px] font-semibold text-text">{p.title}</span>
      <span className="min-w-0 truncate text-[12px]" title={[note, options].filter(Boolean).join("\n")}>
        {note}
        {options && <span className="text-faint"> · {options}</span>}
      </span>
      <span className="flex shrink-0 items-end gap-3 text-[12px]">
        {p.bom ? (
          <Link to={bomLink(p.bom.id)} className="flex items-center gap-1.5 text-text hover:underline">
            BOM Rev {p.bom.revision} {state && <Badge tone={STATE_TONE[state]}>{state}</Badge>}
          </Link>
        ) : (
          can("bom.create") && (
            <button
              type="button"
              onClick={() => createBom(ix, { shopify_product_id: p.link.shopify_product_id })}
              className="flex items-center gap-1 font-medium text-accent-text hover:underline"
            >
              <Plus className="size-3.5" /> Start its BOM
            </button>
          )
        )}
        {can("project.unlink_product") && (
          <button
            type="button"
            title="Take this product out of the project (variants the link put in leave it)"
            onClick={() => confirm(`Take ${p.title} out of ${props.project.name}?`) && mutate("project.unlink_product", { id: p.link.id })}
            className="flex items-center gap-1 text-muted hover:text-text hover:underline"
          >
            <Unlink className="size-3.5" /> Unlink
          </button>
        )}
      </span>
    </span>
  );
}

function ProductPicker({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const products = useRows("shopify_product");
  const links = useRows("project_product");
  const projects = useRows("project");
  const items = useRows("item");
  const [query, setQuery] = useState("");
  const skus = useMemo(() => {
    const out = new Map<string, string[]>();
    for (const i of items) if (i.shopify_product_id && !i.archived) out.set(i.shopify_product_id, [...(out.get(i.shopify_product_id) ?? []), i.sku]);
    return out;
  }, [items]);
  const hits = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    return products
      .filter((p) => !p.deleted && !p.skip_reason)
      .filter((p) => {
        const text = `${p.title} ${p.handle} ${(skus.get(p.id) ?? []).join(" ")}`.toLowerCase();
        return words.every((w) => text.includes(w));
      })
      .slice(0, 20);
  }, [products, query, skus]);
  const linkOf = new Map(links.map((l) => [l.shopify_product_id, l]));
  return (
    <Card
      title="Link a Shopify product"
      note="Search by title, handle or a variant SKU"
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="relative px-4 py-2">
        <Search className="pointer-events-none absolute top-4 left-6.5 size-4 text-faint" />
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. GC Ultimate 2 or GCU1300"
          className="pl-8"
          onKeyDown={(e) => e.key === "Escape" && onClose()}
        />
      </div>
      {hits.map((p) => {
        const l = linkOf.get(p.id);
        const other = l && l.project_id !== projectId ? projects.find((x) => x.id === l.project_id) : undefined;
        return (
          <div key={p.id} className="flex items-center gap-2 border-t border-line px-4 py-2">
            <span className="min-w-0 flex-1 truncate">{p.title}</span>
            <span className="text-[12px] text-muted">
              {p.variant_count} {p.variant_count === 1 ? "variant" : "variants"}
            </span>
            {l && l.project_id === projectId ? (
              <Badge tone="ok">Linked</Badge>
            ) : other ? (
              <Badge>In {other.name}</Badge>
            ) : (
              <Button size="sm" icon={<Link2 className="size-3.5" />} onClick={() => link(projectId, p.id)}>
                Link
              </Button>
            )}
          </div>
        );
      })}
      {query && !hits.length && <div className="border-t border-line px-4 py-3 text-muted">No Shopify product matches.</div>}
    </Card>
  );
}
